import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

const fade = keyframes`from { opacity: 0 } to { opacity: 1 }`
const pop = keyframes`from { opacity: 0; transform: translateY(8px) scale(0.98) } to { opacity: 1; transform: none }`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
  animation: ${fade} 0.15s ease;
`
const Card = styled.div`
  width: 420px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.22);
  overflow: hidden;
  animation: ${pop} 0.18s cubic-bezier(0.2, 0.8, 0.2, 1);
`
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px 14px;
`
const Bar = styled.span`
  width: 4px;
  height: 18px;
  border-radius: 2px;
  background: ${({ theme }) => theme?.colors?.primary || '#D24724'};
`
const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
`
const Body = styled.div`padding: 4px 20px 8px;`
const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#999'};
  margin-bottom: 6px;
`
const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  font-size: 14px;
  color: ${({ theme }) => theme?.colors?.haiti || '#0C1229'};
  border: 1px solid ${({ theme }) => theme?.colors?.mediumGrey || '#e6e6e6'};
  border-radius: 8px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  &:focus {
    border-color: ${({ theme }) => theme?.colors?.primary || '#D24724'};
    box-shadow: 0 0 0 3px ${({ theme }) => theme?.colors?.linen || '#fbede9'};
  }
`
const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 20px 18px;
`
const Ghost = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme?.colors?.grey?.[800] || '#888'};
  font-weight: 600;
  font-size: 14px;
  padding: 9px 14px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme?.colors?.lightGrey || '#f2f2f2'}; }
`
const Primary = styled.button`
  background: ${({ theme }) => theme?.colors?.primary || '#D24724'};
  color: #fff;
  border: none;
  font-weight: 600;
  font-size: 14px;
  padding: 9px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: filter 0.15s;
  &:disabled { opacity: 0.5; cursor: default; }
  &:hover:not(:disabled) { filter: brightness(1.05); }
`

export default function FolderFormModal({ title, label = 'Folder name', initialValue = '', confirmLabel = 'Save', onSubmit, onClose }) {
  const [value, setValue] = useState(initialValue)
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const submit = () => {
    const name = value.trim()
    if (name) onSubmit(name)
  }

  return (
    <Overlay onMouseDown={onClose}>
      <Card onMouseDown={(e) => e.stopPropagation()}>
        <Head>
          <Bar />
          <Title>{title}</Title>
        </Head>
        <Body>
          <Label>{label}</Label>
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit()
              if (e.key === 'Escape') onClose()
            }}
            placeholder="e.g. Insurance certificates"
          />
        </Body>
        <Foot>
          <Ghost onClick={onClose}>Cancel</Ghost>
          <Primary onClick={submit} disabled={!value.trim()}>{confirmLabel}</Primary>
        </Foot>
      </Card>
    </Overlay>
  )
}
